// /procurement/returns — stock we rejected, and what is owed back for it.
//
// ── WHY THIS PAGE IS THE POINT, NOT A REPORT ─────────────────────────────────
// Migration 037 made rejecting units possible. Without this page a return opens
// and NOTHING EVER CLOSES IT: you reject six boards, expect a credit, and there
// is no surface anywhere that says so. Money owed to us, invisible.
//
// That is the whole reason this exists, and it is why the page defaults to the
// WORKLIST (open and shipped) rather than to everything ever raised. A list of
// every return in history is a report somebody opens twice a year. A list of
// what is still outstanding is a thing somebody acts on.
//
// The value column is the same reason. "6 units" does not make anybody chase a
// vendor. "6 units, $2,700" does.
//
// ── EVERY ROW HAS ITS NEXT MOVE IN THE ROW ───────────────────────────────────
// A worklist whose only verb is "write off" is a list of things you give up on.
// An `open` return can go on a manifest (which is what physically sends it) and
// a `shipped` one is waiting on exactly one fact, the vendor's answer, so both
// of those live in the actions column. Nobody leaves this page to close a row.
//
// The two endings are `replaced` and `refunded`, and they are separate on
// purpose: both release the held stock out of QC hold, but only one of them is
// money. Collapsing them into the old `credited` lost that, which is why 071
// dropped it.
//
// ── ONLY ONE OF THE TWO IS A BUTTON ──────────────────────────────────────────
// Replaced is somebody's word: the good units turned up, nothing else happened.
// Refunded is not, and it never was — a credit note arrived, and that note is a
// vendor invoice with a number, a date and an amount that Accounts Payable has
// to see. Marking a row "refunded" without recording that document left money
// visible on this page and invisible in the books.
//
// So the shipped row raises the credit memo instead, and the return closes when
// the bookkeeper confirms it. This page never writes `refunded` itself.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { FilterBar, FilterSelect } from '../components/FilterBar';
import { DataTable } from '../components/table/DataTable';
// Name-first. No SKU here either: a return is chased by a person who knows the
// thing, not the code. The box barcode below IS the identifier that matters.
import ItemName, { itemLabel } from '../components/ItemName';
import ManifestChoice from './BoardReturns/ManifestChoice';
import DrawerShell from '../Receiving/DrawerShell';
import { modalLabel } from '../components/review/fieldStyles';
import {
  RETURN_STATUS_LABELS,
  type PurchaseReturnStatus,
} from './BoardReturns/boardReturnsApi';
import useUserStore from '../stores/useUserStore';
import { formatCents } from './poApi';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { Field } from '../components/ui/Field';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

interface PurchaseReturnRow {
  id: string;
  purchase_order_line_id: string | null;
  receipt_item_id: string | null;
  part_catalog_id: number;
  quantity: number;
  resolution: 'return_to_vendor' | 'scrap';
  reason: string;
  status: PurchaseReturnStatus;
  discovered_at_receipt: boolean;
  vendor_id: string | null;
  unit_cost_cents: number | null;
  notes: string | null;
  created_at: string;
  resolved_at: string | null;
  // Joined by the service:
  sku: string | null;
  part_description: string | null;
  /** Migration 068, joined by purchase-returns-service. `base_name` is what the
   *  thing is, `variant_detail` which one of them, `display_name` the two
   *  already joined. Optional the way every other catalog-joined type declares
   *  them: a payload cached before 068 still parses. */
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  vendor_name: string | null;
  barcode: string | null;
  /** The return's line on the most recent non-cancelled manifest, or nulls.
   *  This is the ONLY place a person can see that a return is already in a
   *  carton, so it rides the vendor cell rather than a column of its own. */
  manifest_id: string | null;
  manifest_number: string | null;
  manifest_status: string | null;
}

/** One row of GET /procurement/returns/:id/credit `{ ok, credits: [...] }`.
 *
 *  A LIST, not one memo: a return can be credited, voided and credited again,
 *  and the route hands back every line pointing at it (discarded documents
 *  aside) newest first. `void` means raised and then killed, so a return whose
 *  only credits are void is owed one again and the action comes back. */
interface ReturnCreditLine {
  vendor_invoice_id: string;
  vendor_invoice_line_id: string;
  amount_cents: number | null;
  invoice_number: string | null;
  invoice_date: string | null; // YYYY-MM-DD
  status: string;
  confirmed_at: string | null;
  voided_at: string | null;
}

/**
 * The chip. A tonal pill on the high surface step, with color on the two
 * statuses that are still somebody's problem: amber for a return nobody has put
 * in a carton yet, blue for one the vendor is sitting on. The four settled
 * statuses take the plain text tone, because a finished row should not compete
 * with a live one for a reader's eye.
 */
const STATUS_CHIP: Record<PurchaseReturnStatus, string> = {
  open: 'bg-surface-container-high text-amber-300',
  shipped: 'bg-surface-container-high text-blue-300',
  replaced: 'bg-surface-container-high text-on-surface',
  refunded: 'bg-surface-container-high text-on-surface',
  scrapped: 'bg-surface-container-high text-on-surface-variant',
  cancelled: 'bg-surface-container-high text-on-surface-variant',
};

/** Whole days since a timestamp. Age is the pressure on this page. */
function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86400000));
}

/**
 * The one thing a cell here cannot answer from its own row: which row is
 * mid-write, and what the verbs do. It changes on a click, so it travels by
 * the table's `meta` rather than in a rebuilt columns array.
 */
interface ReturnsMeta {
  busyId: string | null;
  /** Whether a LIVE credit memo already exists, keyed by return id. A key that
   *  is absent is a lookup still in flight or a row that never needed one. */
  credited: Record<string, boolean>;
  /** Whether THIS user may raise the memo. The credit-memo route is behind the
   *  strict financial gate, which admin does not bypass, so offering the action
   *  to a buyer would be offering a 403. */
  canRecordCredit: boolean;
  onScrap: (row: PurchaseReturnRow) => void;
  onAddToManifest: (row: PurchaseReturnRow) => void;
  onCancel: (row: PurchaseReturnRow) => void;
  onResolve: (row: PurchaseReturnRow) => void;
  onRecordCredit: (row: PurchaseReturnRow) => void;
}

// ---------------------------------------------------------------------------
// Module scope, per the DataTable contract: a fresh columns array hands
// flexRender a new component TYPE and remounts every cell.
//
// No sorting (no accessorFn, and the page passes no `sortable`): the service
// hands these back newest first, and Age is the pressure this page exists to
// apply — a re-sortable column would let somebody lose that order and read the
// pile as if nothing were old.
// ---------------------------------------------------------------------------
const RETURN_COLUMNS: ColumnDef<PurchaseReturnRow, any>[] = [
  {
    id: 'part',
    header: 'Part',
    // A NAME, not an identifier, so it is allowed to wrap inside its cap. The
    // no-wrap rule belongs to codes, and the only code left in this cell is the
    // box barcode underneath.
    cell: ({ row }) => {
      const r = row.original;
      return (
        <>
          <Link
            to={`/parts-catalog/${r.part_catalog_id}`}
            className="text-primary hover:underline"
          >
            <ItemName size="sm" item={r} className="max-w-xs" />
          </Link>
          {r.barcode && (
            <span className="block text-[11px] text-on-surface-variant font-mono">{r.barcode}</span>
          )}
        </>
      );
    },
  },
  {
    id: 'vendor',
    header: 'Vendor',
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row }) => {
      const r = row.original;
      return (
        <>
          <span className="block">{r.vendor_name || '—'}</span>
          {/* Which carton it is in. A link rather than text: the manifest is
              where somebody goes to print the slip or mark it sent. */}
          {r.manifest_id && r.manifest_number && (
            <Link
              to={`/procurement/board-returns/${r.manifest_id}`}
              className="block font-mono text-[11px] text-primary hover:underline"
            >
              {r.manifest_number}
            </Link>
          )}
        </>
      );
    },
  },
  {
    id: 'quantity',
    header: 'Qty',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.quantity,
  },
  {
    // NOT capability-gated, and deliberately so: the whole page mirrors
    // requireCapability('procurement') on the router, which is the tier that
    // sees purchase-pipeline money. Nothing here is Financial-only.
    id: 'value',
    header: 'Value',
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => {
      const r = row.original;
      if (r.unit_cost_cents === null) return <span className="text-on-surface-variant">—</span>;
      return <span className="text-on-surface">{formatCents(r.unit_cost_cents * r.quantity)}</span>;
    },
  },
  {
    id: 'reason',
    header: 'Why',
    meta: { cellClass: 'text-on-surface-variant max-w-xs' },
    cell: ({ row }) => {
      const r = row.original;
      return (
        <>
          {/* Two lines, then a clip. `title` put the rest of the sentence behind
              a hover, which a touch screen and a keyboard both miss, and this
              column is WHY the units went back. */}
          <span className="block line-clamp-2">{r.reason}</span>
          {!r.discovered_at_receipt && (
            <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
              found later
            </span>
          )}
        </>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CHIP[row.original.status]}`}
      >
        {RETURN_STATUS_LABELS[row.original.status] || row.original.status}
      </span>
    ),
  },
  {
    id: 'age',
    header: 'Age',
    meta: { align: 'right', numeric: true },
    // The amber is per ROW (thirty days is when a return stops being in
    // progress and starts being ignored) and meta.cellClass is per COLUMN, so
    // it rides a span inside the cell rather than the td. Weight carries the
    // state alongside the color, and the word carries it for a reader.
    cell: ({ row }) => {
      const age = daysSince(row.original.created_at);
      const stale = age >= 30;
      return (
        <span className={stale ? 'font-semibold text-amber-400' : 'text-on-surface-variant'}>
          {age}d{stale && <span className="sr-only">, stale</span>}
        </span>
      );
    },
  },
  {
    id: 'action',
    // No label: the column is a couple of quiet verbs on the rows that can take
    // them, and a heading over it would announce the exception as the norm.
    header: '',
    meta: { align: 'right' },
    cell: (info) => {
      const {
        busyId, credited, canRecordCredit,
        onScrap, onAddToManifest, onCancel, onResolve, onRecordCredit,
      } = info.table.options.meta as ReturnsMeta;
      const r = info.row.original;
      const busy = busyId === r.id;

      if (r.status === 'open') {
        return (
          <div className="flex items-center justify-end gap-3">
            {/* Adding to a carton is the ordinary next move and writing off is
                the giving-up one, so the order reads forward. */}
            <button
              type="button"
              className={buttonClass('quiet', 'sm')}
              disabled={busy}
              onClick={() => onAddToManifest(r)}
            >
              Add to manifest
            </button>
            <button
              type="button"
              className={buttonClass('danger-quiet', 'sm')}
              disabled={busy}
              onClick={() => onScrap(r)}
            >
              {busy ? 'Working…' : 'Write off'}
            </button>
            {/* Raised in error. Not a write-off: the units go back on the shelf
                they came off, and the order line closes back up. Last, because
                it undoes the row rather than moving it on. */}
            <button
              type="button"
              className={buttonClass('danger-quiet', 'sm')}
              disabled={busy}
              onClick={() => onCancel(r)}
            >
              Cancel return
            </button>
          </div>
        );
      }

      if (r.status === 'shipped') {
        // A voided memo is not a credit: the row is owed one again, so the
        // action comes back rather than the row reading as finished forever.
        const recorded = !!credited[r.id];
        return (
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              className={buttonClass('quiet', 'sm')}
              disabled={busy}
              onClick={() => onResolve(r)}
            >
              Replaced
            </button>
            {/* Three states, one slot. Text rather than a link in two of them:
                the invoice lives behind the strict financial gate, and most
                people who work this page do not hold it, so a link that bounced
                them to the dashboard would be worse than the sentence. */}
            {recorded ? (
              <span className="text-xs text-on-surface-variant">Credit recorded</span>
            ) : canRecordCredit ? (
              <button
                type="button"
                className={buttonClass('quiet', 'sm')}
                disabled={busy}
                onClick={() => onRecordCredit(r)}
              >
                Record the credit
              </button>
            ) : (
              // Not a refusal, a handoff: the buyer's part is done, and this
              // says who does the next part rather than showing a dead control.
              <span className="text-xs text-on-surface-variant">
                Credit is recorded in Accounts Payable
              </span>
            )}
          </div>
        );
      }

      return null;
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Add to manifest
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The one dialog on this page: pick a carton, add the row to it.
 *
 * Deliberately not a `useConfirm`: this asks a question with an answer in it
 * rather than for a yes, and ManifestChoice already knows how to make that
 * answer good (it preselects the vendor's newest open draft, which is the whole
 * reason two half-full cartons stop going out the door).
 *
 * Rides DrawerShell rather than its own overlay: that is where the focus trap,
 * the mount focus, the Escape key and the focus-restore-on-close already live,
 * and a dialog that only LOOKS modal (aria-modal with focus still in the table
 * behind it) is worse than none for a keyboard user.
 */
const AddToManifestDialog: React.FC<{
  row: PurchaseReturnRow;
  onClose: () => void;
  onAdded: (manifestNumber: string) => void;
}> = ({ row, onClose, onAdded }) => {
  const api = useApi();
  const toast = useToast();
  const [choice, setChoice] = useState<'new' | string>('new');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const res = (await api.post(`/procurement/returns/${row.id}/manifest`, {
        body: { manifest: choice },
      })) as { manifest?: { manifest_number?: string } };
      onAdded(res?.manifest?.manifest_number || 'the manifest');
    } catch (err: any) {
      // The 409 the server sends when this return is already in a carton
      // carries the number of that carton, so its own sentence is better than
      // anything this page could write.
      toast.error(
        err?.data?.error?.message || err?.message || 'Could not add that to a manifest',
      );
      setSaving(false);
    }
  };

  return (
    <DrawerShell title="Add to a manifest" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-on-surface-variant">
          {row.quantity} × {itemLabel(row)}
          {row.vendor_name ? ` back to ${row.vendor_name}` : ''}
        </p>

        <ManifestChoice vendorId={row.vendor_id} value={choice} onChange={setChoice} />

        <div className="flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className={buttonClass('filled')}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </DrawerShell>
  );
};

/**
 * Cancel a return raised in error. A reason is required because a return that
 * was netted out of the order and then un-netted leaves two rows of history
 * that disagree, and the reason is what reconciles them for the next reader.
 */
const CancelReturnDialog: React.FC<{
  row: PurchaseReturnRow;
  onClose: () => void;
  onCancelled: () => void;
}> = ({ row, onClose, onCancelled }) => {
  const api = useApi();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const ready = reason.trim().length > 0 && !saving;

  const submit = async () => {
    if (!ready) return;
    setSaving(true);
    try {
      await api.post(`/procurement/returns/${row.id}/cancel`, { body: { reason: reason.trim() } });
      onCancelled();
    } catch (err: any) {
      // The 409 for a claim still on a manifest names the manifest, so the
      // server's own sentence is the one to show.
      toast.error(err?.data?.error?.message || err?.message || 'Could not cancel that return');
      setSaving(false);
    }
  };

  return (
    <DrawerShell title="Cancel this return?" onClose={onClose} dirty={reason.trim() !== ''}>
      <div className="space-y-4">
        <p className="text-sm text-on-surface-variant">
          {row.quantity} × {itemLabel(row)} go back to the shelf they came off, and the order
          line stops expecting them again. Use this for a return raised by mistake; a return
          the vendor will not honor is a write-off instead.
        </p>
        <label className="block">
          <span className={modalLabel}>Why it was raised in error</span>
          <Field
            thumb
            type="text"
            maxLength={200}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Wrong box picked; the damaged units were on the other shelf."
            data-drawer-autofocus=""
          />
        </label>
        <div className="flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={buttonClass('quiet')}
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ready}
            className={buttonClass('filled')}
          >
            {saving ? 'Cancelling…' : 'Cancel return'}
          </button>
        </div>
      </div>
    </DrawerShell>
  );
};

/** Today, as the local calendar reads it. Not toISOString(): that is UTC, and
 *  an evening in Michigan would default the credit note to tomorrow. */
function todayLocal(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

/**
 * Record the credit note the vendor sent for a shipped return.
 *
 * This is the door between this page and the books. It raises a real vendor
 * credit memo, with the vendor's own note number and date on it, and the return
 * closes as refunded when Accounts Payable confirms that memo. Nothing here
 * marks the return refunded directly, which is the whole point: money that
 * moved has to exist as a document somebody can find later.
 *
 * The amount is not typed. It is quantity × the unit cost already on the
 * return, because a hand-typed figure that disagrees with the order line is a
 * reconciliation nobody asked for. A return with no unit cost cannot be priced
 * at all, so it says so and offers nothing to press.
 */
const RecordCreditDialog: React.FC<{
  row: PurchaseReturnRow;
  onClose: () => void;
  onRecorded: (invoiceId: string) => void;
}> = ({ row, onClose, onRecorded }) => {
  const api = useApi();
  const toast = useToast();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [openedOn] = useState(todayLocal);
  const [invoiceDate, setInvoiceDate] = useState(openedOn);
  const [saving, setSaving] = useState(false);

  const priced = row.unit_cost_cents !== null;
  const ready = priced && invoiceNumber.trim().length > 0 && !!invoiceDate && !saving;

  const submit = async () => {
    if (!ready) return;
    setSaving(true);
    try {
      const res = (await api.post('/procurement/vendor-invoices/credit-memo', {
        body: {
          purchase_return_ids: [row.id],
          invoice_number: invoiceNumber.trim(),
          invoice_date: invoiceDate,
        },
      })) as { invoice?: { id?: string } };
      onRecorded(String(res?.invoice?.id || ''));
    } catch (err: any) {
      // The 409s name the specific thing that is wrong (the return moved on,
      // the vendor does not match, the item has no cost), and the server's
      // sentence is written for the person reading it.
      toast.error(err?.data?.error?.message || err?.message || 'Could not record that credit');
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      title="Record the credit"
      onClose={onClose}
      dirty={priced && (invoiceNumber.trim() !== '' || invoiceDate !== openedOn)}
    >
      <div className="space-y-4">
        <p className="text-sm text-on-surface-variant">
          {row.quantity} × {itemLabel(row)}
          {row.vendor_name ? `, ${row.vendor_name}` : ''}
          {priced && (
            <>
              , amount {row.quantity} × {formatCents(row.unit_cost_cents)}{' '}
              <span className="text-on-surface">
                = {formatCents((row.unit_cost_cents || 0) * row.quantity)}
              </span>
            </>
          )}
        </p>

        {!priced ? (
          <p className="rounded-lg bg-surface-container-high px-3 py-2 text-sm text-on-surface">
            No unit cost on this return; set it on the item first.
          </p>
        ) : (
          <>
            <label className="block">
              <span className={modalLabel}>Credit note number</span>
              <Field
                thumb
                type="text"
                maxLength={100}
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="CN-48812"
                data-drawer-autofocus=""
              />
            </label>
            <label className="block">
              <span className={modalLabel}>Credit date</span>
              <Field
                thumb
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </label>
          </>
        )}

        <div className="flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={buttonClass('quiet')}
          >
            Not now
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ready}
            className={buttonClass('filled')}
          >
            {saving ? 'Recording…' : 'Record credit'}
          </button>
        </div>
      </div>
    </DrawerShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// The page
// ─────────────────────────────────────────────────────────────────────────────

/** Still with the vendor, one way or another. Whether a return ends as a credit
 * or as replacement units is the VENDOR'S answer, given when somebody closes it
 * as Replaced or Refunded, so nothing before that can be filtered on: guessing
 * at raise time would hide real exposure behind an expectation nobody checked. */
const isOutstanding = (r: PurchaseReturnRow) => r.status === 'open' || r.status === 'shipped';

const PurchaseReturnsPage: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const history = useHistory();
  const user = useUserStore((s) => s.user);
  // Mirrors requireCapability('procurement') on the router. A return is a claim
  // against a vendor with money attached, so reads are gated too.
  const canAct = !!(user?.isadmin || user?.can_admin || user?.can_procurement);
  // The invoice pages AND the credit-memo route are behind the STRICT financial
  // gate, which admin does not bypass. Somebody who cannot open one must not be
  // sent to one, and must not be offered an action that would 403.
  const canFinancial = !!user?.can_financial;

  const [rows, setRows] = useState<PurchaseReturnRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'' | PurchaseReturnStatus>('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addingTo, setAddingTo] = useState<PurchaseReturnRow | null>(null);
  const [cancelling, setCancelling] = useState<PurchaseReturnRow | null>(null);
  const [recording, setRecording] = useState<PurchaseReturnRow | null>(null);
  const [credited, setCredited] = useState<Record<string, boolean>>({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const data = (await api.get(`/procurement/returns${qs}`)) as { rows: PurchaseReturnRow[] };
      setRows(data.rows || []);
    } catch (err: any) {
      toast.error(err?.message || 'Could not load returns');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  /**
   * Which shipped returns already have a credit memo against them.
   *
   * The list read does not carry it, and putting it there would cost every
   * caller a join for a fact only the shipped rows use. So it is looked up per
   * shipped row, once per load, and nowhere else: the settled statuses have no
   * action to hide and the open ones cannot have a credit yet.
   *
   * A failed lookup reads as "no credit yet", which is the safe way round: the
   * action stays offered, and the server refuses a second memo.
   */
  useEffect(() => {
    const shipped = rows.filter((r) => r.status === 'shipped');
    if (shipped.length === 0) {
      setCredited({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const found: Record<string, boolean> = {};
      await Promise.all(
        shipped.map(async (r) => {
          try {
            const data = (await api.get(`/procurement/returns/${r.id}/credit`)) as {
              credits?: ReturnCreditLine[];
            };
            // Voided ones do not count: the money was un-promised, so the row
            // is owed a credit again.
            found[r.id] = (data?.credits || []).some((c) => c.status !== 'void');
          } catch {
            found[r.id] = false;
          }
        })
      );
      if (!cancelled) setCredited(found);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // The headline: every return the vendor has not answered yet, valued.
  const owed = useMemo(
    () =>
      rows
        .filter(isOutstanding)
        .reduce((sum, r) => sum + (r.unit_cost_cents || 0) * r.quantity, 0),
    [rows]
  );

  const owedCount = useMemo(() => rows.filter(isOutstanding).length, [rows]);

  const handleScrap = async (row: PurchaseReturnRow) => {
    // The one verb here that destroys value, and it was the only one that fired
    // on a single click. Danger tone puts focus on Keep, not on the gold pill.
    const ok = await confirm({
      tone: 'danger',
      title: 'Write these units off?',
      message:
        `${row.quantity} × ${itemLabel(row)} move to Scrap and the return closes. `
        + 'This cannot be undone.',
      confirmLabel: 'Write off',
    });
    if (!ok) return;

    setBusyId(row.id);
    try {
      await api.post(`/procurement/returns/${row.id}/scrap`, { body: {} });
      toast.success('Written off. The stock moved to Scrap.');
      fetchRows();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Could not scrap that');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * The vendor sent good units instead. The held stock leaves QC hold, which is
   * the irreversible half and therefore the half the confirm sentence leads
   * with: the bookkeeping is a word, but stock leaving hold is a physical claim
   * about a shelf.
   *
   * `replaced` is the ONLY outcome this endpoint takes now. A refund closes the
   * return through its credit memo instead, so that the money exists as a
   * document rather than as a status somebody set.
   */
  const handleResolve = async (row: PurchaseReturnRow) => {
    const ok = await confirm({
      // It says "cannot be undone", so it must not open with the gold pill
      // focused: Enter on a dialog somebody has not read should do nothing.
      tone: 'danger',
      title: 'Vendor sent replacements?',
      message:
        `${row.quantity} unit${row.quantity === 1 ? '' : 's'} of ${itemLabel(row)} leave QC hold now, `
        + 'and the return closes as replaced. This cannot be undone.',
      confirmLabel: 'Replaced',
    });
    if (!ok) return;

    setBusyId(row.id);
    try {
      await api.post(`/procurement/returns/${row.id}/resolve`, { body: { outcome: 'replaced' } });
      toast.success('Marked replaced. The held stock left QC hold.');
      fetchRows();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Could not close that return');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * The memo exists. The return does NOT close here: it closes when the
   * bookkeeper confirms the memo, which is why the toast points at that step
   * instead of claiming the row is done.
   */
  const handleCreditRecorded = (invoiceId: string) => {
    setRecording(null);
    toast.success('Credit recorded. Confirm it in Accounts Payable to close the return.');
    // Only a financial user can reach this dialog at all, so the invoice page is
    // somewhere they can actually land. The id guard is for a malformed reply,
    // not for a permission: /invoices/ with nothing after it is not a page.
    if (invoiceId) {
      history.push(`/invoices/${invoiceId}`);
      return;
    }
    fetchRows();
  };

  if (!canAct) {
    return (
      <PageLayout kind="focus" className="pt-4">
        <p className="text-on-surface-variant">You do not have access to purchasing returns.</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Returns"
        helpTitle="About returns"
        lede="Units we rejected, what is owed back for them, and where each one has got to."
        help={
          <>
            <p>
              When something arrives that is not acceptable, receiving records it as rejected
              rather than pretending it never came. This page is what happens next.
            </p>
            <p className="text-on-surface font-medium">Why the count still says it arrived</p>
            <p>
              The order line still shows what arrived, because the vendor did ship it and the
              delivery record has to stay honest. What went back comes off what that line is
              still expecting, so a replacement lands against the same line instead of reading
              as an over-receipt, and a refund leaves the line short until somebody closes the
              order.
            </p>
            <p className="text-on-surface font-medium">Where the stock is</p>
            <p>
              <span className="text-on-surface">QC Hold:</span> arrived, not accepted, decision
              pending. It does not count toward what you can build.
            </p>
            <p>
              <span className="text-on-surface">Scrap:</span> written off. The units stay in the
              history so you can see what happened, but they count nowhere.
            </p>
            <p className="text-on-surface font-medium">The stages</p>
            <p>
              <span className="text-on-surface">Open:</span> decided, but nothing has physically
              gone back and nothing has been written off. Put it on a manifest to send it.
            </p>
            <p>
              <span className="text-on-surface">Awaiting resolution:</span> the manifest went
              out and the vendor has the units. The stock stays on QC hold until they answer,
              because it is still ours on paper.
            </p>
            <p>
              <span className="text-on-surface">Replaced:</span> the vendor sent good units
              instead. No money moves, and the held stock leaves QC hold.
            </p>
            <p>
              <span className="text-on-surface">Refunded:</span> the credit note is on the
              books and accounting has confirmed it. The held stock leaves QC hold.
            </p>
            <p>
              <span className="text-on-surface">Scrapped:</span> written off instead of sent
              back. <span className="text-on-surface">Cancelled:</span> raised in error.
            </p>
            <p className="text-on-surface font-medium">Manifests</p>
            <p>
              A manifest is one carton going back to one vendor, with a numbered packing
              slip. An open return goes on one from its own row, and joining the carton
              somebody already started beats opening a second one. Marking that manifest
              sent is what moves its returns to awaiting resolution.
            </p>
            <p className="text-on-surface font-medium">Money back</p>
            <p>
              Nobody is asked at raise time whether a credit is coming: the vendor decides
              that. If they send good units instead, close the row as Replaced and you are
              done. If they send a credit note, use Record the credit: that files the note as
              a real vendor credit, with its own number and date, and the return closes as
              refunded once accounting confirms it.
            </p>
            <p>
              Until one of those two answers exists, the return counts in the total at the
              top, because the units are gone and nothing has come back for them yet.
            </p>
          </>
        }
        actions={
          /* Vendor RMA lives here rather than in the menu: it is the tool you
             reach for while looking at this screen. It is a DIFFERENT object,
             not a filter on the table below, so it is somewhere to go. A
             manufacturing tech who cannot open THIS page gets it as a menu entry
             instead — see `vendorRmaDirect` in navigation/menuConfig.js. */
          <SecondaryAction variant="text" to="/procurement/board-returns">
            Vendor RMA
          </SecondaryAction>
        }
      />

      <FilterBar>
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as '' | PurchaseReturnStatus)}
        >
          {/* The empty value is the WORKLIST, not "everything": the server's
              default is open + shipped, and that is the pile this page is for. */}
          <option value="">Outstanding</option>
          <option value="open">{RETURN_STATUS_LABELS.open}</option>
          <option value="shipped">{RETURN_STATUS_LABELS.shipped}</option>
          <option value="replaced">{RETURN_STATUS_LABELS.replaced}</option>
          <option value="refunded">{RETURN_STATUS_LABELS.refunded}</option>
          <option value="scrapped">{RETURN_STATUS_LABELS.scrapped}</option>
          <option value="cancelled">{RETURN_STATUS_LABELS.cancelled}</option>
        </FilterSelect>
      </FilterBar>

      {/* The number this page exists to make visible. */}
      <Reveal show={owedCount > 0}>
        <Alert tone="warning" live={false}>
          <span className="font-semibold text-amber-300">{formatCents(owed)}</span> outstanding
          across {owedCount} return{owedCount === 1 ? '' : 's'} still open with the vendor.
        </Alert>
      </Reveal>

      {/* The vessel is the shared table's: shell, radius, scroll container and
          the face on every row. The page's own box, header band and row borders
          are gone; what stays page-local is the status filter above, which is
          server-side (it re-fetches), and the outstanding banner, which is a
          claim about money rather than a fact about the pile. */}
      <DataTable<PurchaseReturnRow>
        data={rows}
        columns={RETURN_COLUMNS}
        getRowId={(r) => r.id}
        meta={{
          busyId,
          credited,
          canRecordCredit: canFinancial,
          onScrap: handleScrap,
          onAddToManifest: setAddingTo,
          onCancel: setCancelling,
          onResolve: handleResolve,
          onRecordCredit: setRecording,
        }}
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading purchase returns" className="space-y-2 px-4 py-6">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </SkeletonGroup>
        }
        empty={
          // The empty state teaches rather than shrugging. On a filtered view it
          // says what is filtered; on the default view it says what would put a
          // row here, because "no returns" is genuinely good news and should read
          // that way.
          statusFilter ? (
            <EmptyState variant="area">
              Nothing at that status. Switch the filter to Outstanding to see what is live.
            </EmptyState>
          ) : (
            <EmptyState variant="area">
              Nothing outstanding. Everything that arrived was fine.
              <span className="mt-2 block text-xs leading-relaxed text-on-surface-variant">
                A row lands here when somebody rejects units while receiving, or pulls bad ones
                out of a box afterwards from that box&apos;s own page.
              </span>
            </EmptyState>
          )
        }
      />

      {addingTo && (
        <AddToManifestDialog
          row={addingTo}
          onClose={() => setAddingTo(null)}
          onAdded={(manifestNumber) => {
            setAddingTo(null);
            toast.success(`Added to ${manifestNumber}`);
            fetchRows();
          }}
        />
      )}
      {recording && (
        <RecordCreditDialog
          row={recording}
          onClose={() => setRecording(null)}
          onRecorded={handleCreditRecorded}
        />
      )}
      {cancelling && (
        <CancelReturnDialog
          row={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            toast.success('Return cancelled. The stock is back where it came from.');
            fetchRows();
          }}
        />
      )}
    </PageLayout>
  );
};

export default PurchaseReturnsPage;
